import countries from "i18n-iso-countries";
import englishCountries from "i18n-iso-countries/langs/en.json";
import { Address } from "@/types/org";

type AddressLike = Partial<
  Pick<
    Address,
    "addressLine1" | "addressLine2" | "city" | "state" | "zipCode" | "country"
  >
>;

countries.registerLocale(englishCountries);

const normalizeCountryName = (country: string) =>
  country
    .trim()
    .replaceAll(/[‘’]/g, "'")
    .replaceAll(/\s+/g, " ");

export const normalizeCountryCode = (country?: string | null) => {
  const value = country ? normalizeCountryName(country) : "";
  if (!value) {
    return "";
  }

  const countryCode = value.toUpperCase();
  if (/^[A-Z]{2}$/.test(countryCode) && countries.isValid(countryCode)) {
    return countryCode;
  }

  if (/^[A-Z]{3}$/.test(countryCode)) {
    const alpha2 = countries.alpha3ToAlpha2(countryCode);
    if (alpha2) {
      return alpha2;
    }
  }

  return countries.getAlpha2Code(value, "en") ?? value;
};

export const sanitizeAddress = (
  rawAddress?: AddressLike | null
): Address | undefined => {
  if (!rawAddress) {
    return undefined;
  }

  const addressLine1 = rawAddress.addressLine1?.trim() ?? "";
  const city = rawAddress.city?.trim() ?? "";
  const state = rawAddress.state?.trim() ?? "";
  const zipCode = rawAddress.zipCode?.trim() ?? "";
  const country = normalizeCountryCode(rawAddress.country);
  const addressLine2 = rawAddress.addressLine2?.trim() ?? "";

  if (!addressLine1 && !city && !state) {
    return undefined;
  }

  return {
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    country,
  };
};

export const mapSelectedAddress = (address: AddressLike): Address => ({
  addressLine1: address.addressLine1?.trim() ?? "",
  addressLine2: address.addressLine2?.trim() ?? "",
  city: address.city?.trim() ?? "",
  state: address.state?.trim() ?? "",
  zipCode: address.zipCode?.trim() ?? "",
  country: normalizeCountryCode(address.country),
});

export const areAddressesEqual = (a?: Address | null, b?: Address | null) => {
  if (!a || !b) {
    return !a && !b;
  }

  return (
    a.addressLine1 === b.addressLine1 &&
    a.addressLine2 === b.addressLine2 &&
    a.city === b.city &&
    a.state === b.state &&
    a.zipCode === b.zipCode &&
    a.country === b.country
  );
};
